import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Building2, Upload } from "lucide-react";

/**
 * Modal de criação rápida de empresa (usado no Dashboard Consolidado).
 * Props: open, onOpenChange, onCreated
 */
export function NovaEmpresaDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({ nome: "", cnpj: "", logo_base64: "" });
  const [saving, setSaving] = useState(false);

  const onLogo = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 500 * 1024) return toast.error("Logo maior que 500KB");
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, logo_base64: reader.result }));
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    if (!form.nome.trim()) return toast.error("Nome é obrigatório");
    setSaving(true);
    try {
      const r = await api.post("/empresas", form);
      toast.success(`Empresa "${r.data.nome}" criada · tenant limpo pronto para uso`);
      setForm({ nome: "", cnpj: "", logo_base64: "" });
      onOpenChange(false);
      onCreated?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao criar empresa");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="dialog-nova-empresa">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Building2 className="w-5 h-5 text-orange-600" />Nova Empresa</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Nome <span className="text-rose-600">*</span></Label>
            <Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} placeholder="Ex: Drogaria XYZ" data-testid="nova-empresa-nome" autoFocus />
          </div>
          <div>
            <Label>CNPJ</Label>
            <Input value={form.cnpj} onChange={(e) => setForm({ ...form, cnpj: e.target.value })} placeholder="00.000.000/0001-00" data-testid="nova-empresa-cnpj" />
          </div>
          <div>
            <Label>Logo (opcional, ≤500KB)</Label>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 h-9 px-3 border-2 border-dashed border-orange-300 rounded-md cursor-pointer hover:bg-orange-50 text-sm text-orange-700 flex-1 justify-center">
                <Upload className="w-4 h-4" />
                <span>{form.logo_base64 ? "Trocar logo" : "Selecionar imagem"}</span>
                <input type="file" accept="image/*" hidden onChange={onLogo} data-testid="nova-empresa-logo-input" />
              </label>
              {form.logo_base64 && <img src={form.logo_base64} alt="preview" className="w-14 h-14 object-contain border rounded bg-white" data-testid="nova-empresa-logo-preview" />}
            </div>
          </div>
          <div className="text-xs bg-orange-50 border border-orange-200 rounded p-2 text-orange-800">
            <b>Tenant limpo:</b> a nova empresa começa sem entregas nem usuários. Cadastre um usuário admin em <b>Usuários</b> depois e vincule esta empresa a ele.
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button onClick={submit} disabled={saving} className="bg-orange-600 hover:bg-orange-700" data-testid="btn-criar-empresa">
              {saving ? "Criando..." : "Criar Empresa"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
